import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  Typography,
  Grid,
  Paper,
  Box,
  Divider,
  Card,
  Container,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  CircularProgress,
  alpha
} from '@mui/material';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Loader from '../components/Loader';
import {
  School,
  LocationOn,
  Phone,
  Email,
  Cake,
  LinkedIn,
  GitHub,
  Code,
  Description,
  Edit,
  CalendarMonth,
  OpenInNew,
  WorkOutline,
  Add,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CardMembership, // for certificate icon
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Check,
  Business,
  QuestionAnswer,
  Announcement
} from '@mui/icons-material';
import AddSkillsDialog from '../components/AddSkillsDialog';
import EditExperienceDialog from '../components/EditExperienceDialog';
import AddCertificateDialog from '../components/AddCertificateDialog';
import EditCertificateDialog from '../components/EditCertificateDialog';
import { toast } from 'react-toastify';

// Function to ensure a URL is absolute
const ensureAbsoluteUrl = (url) => {
  if (!url) return '#';
  return url.match(/^https?:\/\//i) ? url : `https://${url}`;
};

const Profile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const userId = localStorage.getItem('userId')?.trim();
  const token = localStorage.getItem('token')?.trim();
  const navigate = useNavigate();

  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [currentExperience, setCurrentExperience] = useState(null);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [experienceToDelete, setExperienceToDelete] = useState(null);

  const [openCertificateDialog, setOpenCertificateDialog] = useState(false);
  const [openEditCertificateDialog, setOpenEditCertificateDialog] = useState(false);
  const [currentCertificate, setCurrentCertificate] = useState(null);
  const [certificateToDelete, setCertificateToDelete] = useState(null);
  const [openDeleteCertificateConfirm, setOpenDeleteCertificateConfirm] = useState(false);

  const [selectedForms, setSelectedForms] = useState([]);
  const [loadingSelectedForms, setLoadingSelectedForms] = useState(false);

  useEffect(() => {
    if (userId && token) {
      const fetchUserData = async () => {
        try {
          const userResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}api/users/profile/${userId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(userResponse.data);
          setLoading(false);
        } catch (error) {
          console.error('Error fetching user data:', error);
          setLoading(false);
        }
      };

      fetchUserData();
    }
  }, [userId, token]);

  useEffect(() => {
    if (userId && token) {
      setLoadingSelectedForms(true);
      axios.get(`${process.env.REACT_APP_BACKEND_URL}api/users/selected-students/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(response => {
          setSelectedForms(response.data.forms || []);
          setLoadingSelectedForms(false);
        })
        .catch(error => {
          console.error('Error fetching selected forms:', error);
          setLoadingSelectedForms(false);
        });
    }
  }, [userId, token]);

  const handleEditProfile = () => {
    navigate('/edit-profile');
  };

  const handleAddExperience = async (experience) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}api/users/add-experience/${userId}`,
        experience,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200) {
        console.log('New Skill Added:', response.data);
        toast.success('Experience added successfully!', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        window.location.reload();
      } else {
        toast.error('Failed to add experience. Please try again.', {
          position: "top-right",
          autoClose: 3000,
        });
      }
      setOpenDialog(false);
    } catch (error) {
      console.error('Error adding experience:', error);

      toast.error(error.response?.data?.message || 'Failed to add experience', {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleEditExperience = async (updatedExperience) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}api/users/update-experience/${userId}/${updatedExperience._id}`,
        updatedExperience,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200) {
        console.log('Experience Updated:', response.data);
        toast.success('Experience updated successfully!', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        window.location.reload();
      } else {
        toast.error('Failed to update experience. Please try again.', {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error('Error updating experience:', error);
      toast.error(error.response?.data?.message || 'Failed to update experience', {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleDeleteExperience = async () => {
    if (!experienceToDelete?._id) return;

    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}api/users/delete-experience/${userId}/${experienceToDelete._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        console.log('Experience Deleted:', response.data);
        setOpenDeleteConfirm(false);
        setExperienceToDelete(null);
        window.location.reload();
      } else {
        toast.error('Failed to delete experience. Please try again.', {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error('Error deleting experience:', error);
    }
  };

  const handleAddCertificate = async (certificate) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}api/users/add-certificate/${userId}`,
        certificate,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200) {
        console.log('New Certificate Added:', response.data);
        toast.success('Certificate added successfully!', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        window.location.reload();
      } else {
        toast.error('Failed to add certificate. Please try again.', {
          position: "top-right",
          autoClose: 3000,
        });
      }
      setOpenCertificateDialog(false);
    } catch (error) {
      console.error('Error adding certificate:', error);

      toast.error(error.response?.data?.message || 'Failed to add certificate', {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleEditCertificate = async (updatedCertificate) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}api/users/update-certificate/${userId}/${updatedCertificate._id}`,
        updatedCertificate,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200) {
        console.log('Certificate Updated:', response.data);
        toast.success('Certificate updated successfully!', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        window.location.reload();
      } else {
        toast.error('Failed to update certificate. Please try again.', {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error('Error updating certificate:', error);
      toast.error(error.response?.data?.message || 'Failed to update certificate', {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleDeleteCertificate = async () => {
    if (!certificateToDelete?._id) return;

    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}api/users/delete-certificate/${userId}/${certificateToDelete._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        console.log('Certificate Deleted:', response.data);
        setOpenDeleteCertificateConfirm(false);
        setCertificateToDelete(null);
        window.location.reload();
      } else {
        toast.error('Failed to delete certificate. Please try again.', {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error('Error deleting certificate:', error);
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (!user) {
    return (
      <Container>
        <Typography variant="h5" color="error" align="center" sx={{ mt: 8 }}>
          Unable to load profile data. Please try again later.
        </Typography>
      </Container>
    );
  }

  const formatEnrollmentNumber = (enrollmentNumber, expectedLength = 11) => {
    if (!enrollmentNumber) return enrollmentNumber;
    const strNumber = String(enrollmentNumber);
    if (strNumber.length === expectedLength) return strNumber;
    if (strNumber.length < expectedLength) {
      return strNumber.padStart(expectedLength, '0');
    }
    return strNumber;
  };

  // Check if URL exists and is not empty
  const hasValidUrl = (url) => {
    return url && url.trim() !== '';
  };

  const formTypeConfig = {
    interview: {
      icon: <Business sx={{ color: '#1976d2' }} />,
      color: '#3f51b5',
      label: 'Interview',
      bgColor: 'rgba(63, 81, 181, 0.1)'
    },
    hackathon: {
      icon: <Code sx={{ color: '#1976d2' }} />,
      color: '#f50057',
      label: 'Hackathon',
      bgColor: 'rgba(245, 0, 87, 0.1)'
    },
    test: {
      icon: <QuestionAnswer sx={{ color: '#1976d2' }} />,
      color: '#ff9800',
      label: 'Test',
      bgColor: 'rgba(255, 152, 0, 0.1)'
    },
    general: {
      icon: <Announcement sx={{ color: '#1976d2' }} />,
      color: '#4caf50',
      label: 'General',
      bgColor: 'rgba(76, 175, 80, 0.1)'
    }
  };

  return (
    <>
      <Navbar />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        <Box sx={{ position: 'relative', mb: 6 }}>
          {/* Cover Photo */}
          <Box
            sx={{
              height: 200,
              borderRadius: 2,
              backgroundColor: '#1976d2',
              backgroundImage: 'linear-gradient(135deg, #1976d2 0%, #64b5f6 100%)',
              mb: -10,
              boxShadow: 3
            }}
          />

          {/* Profile Picture and Name Section */}
          <Paper
            elevation={3}
            sx={{
              p: 3,
              mx: 2,
              borderRadius: 2,
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4} md={3} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Avatar
                  src={user.photoURL || 'https://source.unsplash.com/random/?portrait'}
                  alt={user.fullname}
                  sx={{
                    width: 150,
                    height: 150,
                    border: '4px solid white',
                    boxShadow: 2,
                    mb: 2
                  }}
                />
                <Box sx={{ textAlign: 'center', width: '100%' }}>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    {user.fullname}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    Enrollment:
                    <a
                      href={`https://www.ipuranklist.com/student/${formatEnrollmentNumber(user.enrollmentNumber)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {` ${formatEnrollmentNumber(user.enrollmentNumber)}`}
                    </a>
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                    {hasValidUrl(user.linkedin) && (
                      <Tooltip title="LinkedIn">
                        <IconButton
                          color="primary"
                          component="a"
                          href={ensureAbsoluteUrl(user.linkedin)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <LinkedIn />
                        </IconButton>
                      </Tooltip>
                    )}
                    {hasValidUrl(user.github) && (
                      <Tooltip title="GitHub">
                        <IconButton
                          color="primary"
                          component="a"
                          href={ensureAbsoluteUrl(user.github)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <GitHub />
                        </IconButton>
                      </Tooltip>
                    )}
                    {hasValidUrl(user.leetCode) && (
                      <Tooltip title="LeetCode">
                        <IconButton
                          color="primary"
                          component="a"
                          href={ensureAbsoluteUrl(user.leetCode)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Code />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleEditProfile}
                    startIcon={<Edit />}
                    sx={{ mt: 3 }}
                    fullWidth
                  >
                    Edit Profile
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} sm={8} md={9}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h4" fontWeight="bold">
                    Student Profile
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  {/* Personal Information */}
                  <Grid item xs={12} md={6}>
                    <Card elevation={2} sx={{ p: 2, height: '100%' }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                        <Person sx={{ mr: 1 }} fontSize="small" /> Personal Information
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Email fontSize="small" color="action" sx={{ mr: 1.5 }} />
                        <Typography variant="body1">{user.email}</Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Phone fontSize="small" color="action" sx={{ mr: 1.5 }} />
                        <Typography variant="body1">{user.contactNumber || 'Not provided'}</Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Cake fontSize="small" color="action" sx={{ mr: 1.5 }} />
                        <Typography variant="body1">
                          {user.dob ? new Date(user.dob).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          }) : 'Not provided'}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <LocationOn fontSize="small" color="action" sx={{ mr: 1.5 }} />
                        <Typography variant="body1">{user.address || 'Not provided'}</Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Public fontSize="small" color="action" sx={{ mr: 1.5 }} />
                        <Typography variant="body1">{user.nationality || 'Not provided'}</Typography>
                      </Box>
                    </Card>
                  </Grid>

                  {/* Academic Information */}
                  <Grid item xs={12} md={6}>
                    <Card elevation={2} sx={{ p: 2, height: '100%' }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                        <School sx={{ mr: 1 }} fontSize="small" /> Academic Information
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <School fontSize="small" color="action" sx={{ mr: 1.5 }} />
                        <Typography variant="body1">
                          <strong>{user.degree || 'Not specified'}</strong> in {user.course || 'Not specified'}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Class fontSize="small" color="action" sx={{ mr: 1.5 }} />
                        <Typography variant="body1">Class: {user.classes || 'Not specified'}</Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Grade fontSize="small" color="action" sx={{ mr: 1.5 }} />
                        <Typography variant="body1">CGPA: {user.cgpa || 'Not specified'}</Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <CalendarMonth fontSize="small" color="action" sx={{ mr: 1.5 }} />
                        <Typography variant="body1">Graduation Year: {user.yearOfPassing || 'Not specified'}</Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Report fontSize="small" color="action" sx={{ mr: 1.5 }} />
                        <Typography variant="body1">Active Backlogs: {user.activeBacklogs !== undefined ? user.activeBacklogs : 'Not specified'}</Typography>
                      </Box>
                    </Card>
                  </Grid>

                  {/* Academic Records */}
                  <Grid item xs={12}>
                    <Card elevation={2} sx={{ p: 2 }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                        <WorkOutline sx={{ mr: 1 }} fontSize="small" /> Previous Education
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <Box sx={{
                            p: 2,
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            textAlign: 'center',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                          }}>
                            <Typography variant="subtitle2" color="text.secondary">10th Percentage</Typography>
                            <Typography variant="h5" fontWeight="medium" color="primary">
                              {user.tenthPercentage ? `${user.tenthPercentage}%` : 'N/A'}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <Box sx={{
                            p: 2,
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            textAlign: 'center',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                          }}>
                            <Typography variant="subtitle2" color="text.secondary">12th Percentage</Typography>
                            <Typography variant="h5" fontWeight="medium" color="primary">
                              {user.twelfthPercentage ? `${user.twelfthPercentage}%` : 'N/A'}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <Box sx={{
                            p: 2,
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            textAlign: 'center',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                          }}>
                            <Typography variant="subtitle2" color="text.secondary">Diploma Percentage</Typography>
                            <Typography variant="h5" fontWeight="medium" color="primary">
                              {user.diplomaPercentage ? `${user.diplomaPercentage}%` : 'N/A'}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      <Box sx={{ mt: 3 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                              <WatchLater fontSize="small" color="action" sx={{ mr: 1.5 }} />
                              <Typography variant="body1">Gap Year(s): {user.gapYear !== undefined ? user.gapYear : 'Not specified'}</Typography>
                            </Box>
                          </Grid>

                          {user.school12th && (
                            <Grid item xs={12} md={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                <School fontSize="small" color="action" sx={{ mr: 1.5 }} />
                                <Typography variant="body1">12th School: {user.school12th}</Typography>
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Card>
                  </Grid>

                  {/* Resume */}
                  {hasValidUrl(user.resumeURL) && (
                    <Grid item xs={12}>
                      <Card elevation={2} sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                            <Description sx={{ mr: 1 }} fontSize="small" /> Resume
                          </Typography>
                          <Button
                            variant="outlined"
                            color="primary"
                            component="a"
                            href={ensureAbsoluteUrl(user.resumeURL)}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<OpenInNew />}
                          >
                            View Resume
                          </Button>
                        </Box>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Skills & Experiences Section */}
        <Box sx={{ mt: 4, mx: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" fontWeight="bold">
              Experience
            </Typography>
            <Box>
              <IconButton onClick={() => setOpenDialog(true)} size="small" sx={{ mr: 1 }}>
                <Add />
              </IconButton>
            </Box>
          </Box>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            {user.experience && user.experience.length > 0 ? (
              user.experience.map((skill, index) => (
                <Box
                  key={index}
                  className="gap-5 max-sm:gap-0"
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    pb: 2,
                    pt: index != 0 && 2,
                    position: 'relative',
                    overflow: "hidden",
                    borderBottom: index !== user.experience.length - 1 ? '1px solid #e0e0e0' : 'none',
                  }}
                >
                  {/* Left Section - Company Info */}
                  <Box sx={{ width: { xs: '100%', md: '50%' }, display: 'flex', gap: 2 }}>
                    {/* Company Logo */}
                    <Avatar
                      src={skill.logo || ''}
                      alt={skill.company}
                      variant="rounded"
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: skill.logo ? 'transparent' : '#f0f0f0',
                        alignSelf: 'flex-start',
                        color: "black"
                      }}
                    >
                      {!skill.logo && skill.company?.charAt(0)}
                    </Avatar>

                    {/* Company Details */}
                    <Box>
                      {/* Job Title */}
                      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem', mb: 0.5 }}>
                        {skill.title || 'No Title'}
                      </Typography>

                      {/* Company & Employment Type */}
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {skill.company || 'No Company Name'} · {skill.employmentType || 'Not Specified'}
                      </Typography>

                      {/* Date Range & Duration */}
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {skill.startDate &&
                          `${new Date(skill.startDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} - `}
                        {skill.endDate ?
                          new Date(skill.endDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) :
                          'Present'}
                        {skill.startDate && skill.endDate &&
                          ` · ${Math.ceil((new Date(skill.endDate) - new Date(skill.startDate)) / (30.44 * 24 * 60 * 60 * 1000))} mos`}
                      </Typography>

                      {/* Location */}
                      <Typography variant="body2" color="text.secondary">
                        {skill.location || 'Remote'}
                      </Typography>

                      {/* Description with Tooltip */}
                      {skill.description && (
                        <Tooltip title={skill.description} arrow>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mb: 2,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            <strong>Description:</strong> {skill.description}
                          </Typography>
                        </Tooltip>
                      )}

                    </Box>
                  </Box>

                  {/* Right Section - Skills & Details */}
                  <Box sx={{ width: { xs: '100%', md: '50%' } }}>
                    {/* All Skills */}
                    {skill.skills && skill.skills.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                          <strong>Skills:</strong>
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {skill.skills.map((skillItem, i) => (
                            skillItem && (
                              <Box
                                key={i}
                                component="span"
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  bgcolor: '#f0f7ff',
                                  color: '#1976d2',
                                  borderRadius: 1,
                                  px: 1,
                                  py: 0.5,
                                  fontSize: '0.75rem',
                                  fontWeight: 'medium',
                                }}
                              >
                                {skillItem}
                              </Box>
                            )
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Links */}
                    {skill.links && skill.links.length > 0 && skill.links[0] && (
                      <Box>
                        <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                          <strong>Links:</strong>
                        </Typography>
                        {skill.links.map((link, i) => (
                          link && (
                            <Typography
                              key={i}
                              variant="body2"
                              component="a"
                              href={ensureAbsoluteUrl(link)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className='w-fit'
                              sx={{
                                display: 'block',
                                color: '#1976d2',
                                textDecoration: 'underline',
                                mb: 0.5,
                                wordBreak: 'break-all',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '80%'
                              }}
                            >
                              {link}
                            </Typography>
                          )
                        ))}
                      </Box>
                    )}
                  </Box>

                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    mt: { xs: 2, md: 0 },
                    position: { xs: 'relative', md: 'absolute' },
                    right: { md: 10 },
                    bottom: { md: 10 },
                    width: { xs: '100%', md: 'auto' },
                    gap: 1,
                    zIndex: 2
                  }}>
                    {/* Edit button */}
                    <IconButton
                      size="small"
                      sx={{
                        bgcolor: '#f5f5f5',
                        '&:hover': {
                          bgcolor: '#e0e0e0',
                        },
                      }}
                      onClick={() => {
                        setCurrentExperience(skill);
                        setOpenEditDialog(true);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>

                    {/* Delete button */}
                    <IconButton
                      size="small"
                      color="error"
                      sx={{
                        bgcolor: '#fff0f0',
                        '&:hover': {
                          bgcolor: '#ffe0e0',
                        },
                      }}
                      onClick={() => {
                        setExperienceToDelete(skill);
                        setOpenDeleteConfirm(true);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No skills or experiences added yet.
              </Typography>
            )}
          </Paper>
        </Box>

        {/* Certificates Section */}
        <Box sx={{ mt: 4, mx: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" fontWeight="bold">
              Certificates
            </Typography>
            <Box>
              <IconButton onClick={() => setOpenCertificateDialog(true)} size="small" sx={{ mr: 1 }}>
                <Add />
              </IconButton>
            </Box>
          </Box>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            {user.certificates && user.certificates.length > 0 ? (
              user.certificates.map((certificate, index) => (
                <Box
                  key={index}
                  className="gap-5 max-sm:gap-0"
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    pb: 2,
                    pt: index != 0 && 2,
                    position: 'relative',
                    overflow: "hidden",
                    borderBottom: index !== user.certificates.length - 1 ? '1px solid #e0e0e0' : 'none',
                  }}
                >
                  {/* Left Section - Certificate Info */}
                  <Box sx={{ width: { xs: '100%', md: '50%' }, display: 'flex', gap: 2 }}>
                    {/* Certificate Icon */}
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: '#e3f2fd',
                        alignSelf: 'flex-start'
                      }}
                    >
                      <CardMembership sx={{ color: '#1976d2' }} />
                    </Avatar>

                    {/* Certificate Details */}
                    <Box>
                      {/* Certificate Name */}
                      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem', mb: 0.5 }}>
                        {certificate.name || 'No Name'}
                      </Typography>

                      {/* Issuing Organization */}
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {certificate.issuingOrganization || 'No Organization'}
                      </Typography>

                      {/* Issue Date */}
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Issued: {certificate.issueDate ?
                          new Date(certificate.issueDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) :
                          'Not specified'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Right Section - Skills & Links */}
                  <Box sx={{ width: { xs: '100%', md: '50%' } }}>
                    {/* Certificate Skills */}
                    {certificate.skills && certificate.skills.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                          <strong>Skills:</strong>
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {certificate.skills.map((skillItem, i) => (
                            skillItem && (
                              <Box
                                key={i}
                                component="span"
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  bgcolor: '#f0f7ff',
                                  color: '#1976d2',
                                  borderRadius: 1,
                                  px: 1,
                                  py: 0.5,
                                  fontSize: '0.75rem',
                                  fontWeight: 'medium',
                                }}
                              >
                                {skillItem}
                              </Box>
                            )
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Certificate Links */}
                    {certificate.links && certificate.links.length > 0 && certificate.links[0] && (
                      <Box>
                        <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                          <strong>Links:</strong>
                        </Typography>
                        {certificate.links.map((link, i) => (
                          link && (
                            <Typography
                              key={i}
                              variant="body2"
                              component="a"
                              href={ensureAbsoluteUrl(link)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className='w-fit'
                              sx={{
                                display: 'block',
                                color: '#1976d2',
                                textDecoration: 'underline',
                                mb: 0.5,
                                wordBreak: 'break-all',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '80%'
                              }}
                            >
                              {link}
                            </Typography>
                          )
                        ))}
                      </Box>
                    )}
                  </Box>

                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    mt: { xs: 2, md: 0 },
                    position: { xs: 'relative', md: 'absolute' },
                    right: { md: 10 },
                    bottom: { md: 10 },
                    width: { xs: '100%', md: 'auto' },
                    gap: 1,
                    zIndex: 2
                  }}>
                    {/* Edit button */}
                    <IconButton
                      size="small"
                      sx={{
                        bgcolor: '#f5f5f5',
                        '&:hover': {
                          bgcolor: '#e0e0e0',
                        },
                      }}
                      onClick={() => {
                        setCurrentCertificate(certificate);
                        setOpenEditCertificateDialog(true);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>

                    {/* Delete button */}
                    <IconButton
                      size="small"
                      color="error"
                      sx={{
                        bgcolor: '#fff0f0',
                        '&:hover': {
                          bgcolor: '#ffe0e0',
                        },
                      }}
                      onClick={() => {
                        setCertificateToDelete(certificate);
                        setOpenDeleteCertificateConfirm(true);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No certificates added yet.
              </Typography>
            )}
          </Paper>
        </Box>

        {/* My Applications Section */}
        <Box sx={{ mt: 4, mx: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentIcon color="primary" /> My Applications
            </Typography>
          </Box>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            {user.appliedForms && user.appliedForms.length > 0 ? (
              user.appliedForms.map((form, index) => (
                <Box
                  key={form._id || index}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    pb: 2,
                    pt: index !== 0 && 2,
                    position: 'relative',
                    overflow: "hidden",
                    borderBottom: index !== user.appliedForms.length - 1 ? '1px solid #e0e0e0' : 'none',
                  }}
                >
                  <Box sx={{ width: { xs: '100%', md: '50%' }, display: 'flex', gap: 2 }}>
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: "#e3f2fd",
                        alignSelf: 'flex-start'
                      }}
                    >
                      {formTypeConfig[form.formType]?.icon || <AssignmentIcon sx={{ color: '#1976d2' }} />}
                    </Avatar>

                    <Box>
                      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem', mb: 0.5 }}>
                        {form.title || 'No Title'}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {form.companyName || ''}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {form.createdAt ? `Applied: ${new Date(form.createdAt).toLocaleDateString('en-US', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}` : ''}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <Chip
                          label={formTypeConfig[form.formType]?.label || form.formType}
                          size="small"
                          sx={{
                            bgcolor: formTypeConfig[form.formType]?.bgColor || '#e3f2fd',
                            color: formTypeConfig[form.formType]?.color || '#1976d2',
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ width: { xs: '100%', md: '50%' }, mt: { xs: 2, md: 0 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        color="primary"
                        onClick={() => navigate(`/form/${form._id}`)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          borderColor: formTypeConfig[form.formType]?.color || '#1976d2',
                          color: formTypeConfig[form.formType]?.color || '#1976d2',
                          '&:hover': {
                            borderColor: formTypeConfig[form.formType]?.color || '#1976d2',
                            backgroundColor: alpha(formTypeConfig[form.formType]?.color || '#1976d2', 0.08)
                          }
                        }}
                      >
                        View Details
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                You haven't applied to any opportunities yet.
              </Typography>
            )}
          </Paper>
        </Box>

        {/* Selected For Section */}
        <Box sx={{ mt: 4, mx: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon sx={{ color: '#4caf50' }} /> I am Selected
            </Typography>
          </Box>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            {selectedForms && selectedForms.length > 0 ?
              selectedForms.map((form, index) => (
                <Box
                  key={form._id || index}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    pb: 2,
                    pt: index !== 0 && 2,
                    position: 'relative',
                    overflow: "hidden",
                    borderBottom: index !== user.appliedForms.length - 1 ? '1px solid #e0e0e0' : 'none',
                  }}
                >
                  <Box sx={{ width: { xs: '100%', md: '50%' }, display: 'flex', gap: 2 }}>
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: "#e3f2fd",
                        alignSelf: 'flex-start'
                      }}
                    >
                      {formTypeConfig[form.formType]?.icon || <CheckCircleIcon sx={{ color: '#4caf50' }} />}
                    </Avatar>

                    <Box>
                      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem', mb: 0.5 }}>
                        {form.title || 'No Title'}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {form.companyName || ''}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {form.createdAt ? `Applied: ${new Date(form.createdAt).toLocaleDateString('en-US', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}` : ''}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <Chip
                          label={formTypeConfig[form.formType]?.label || form.formType}
                          size="small"
                          sx={{
                            bgcolor: formTypeConfig[form.formType]?.bgColor || '#e3f2fd',
                            color: formTypeConfig[form.formType]?.color || '#1976d2',
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ width: { xs: '100%', md: '50%' }, mt: { xs: 2, md: 0 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        color="success"
                        onClick={() => navigate(`/form/${form._id}`)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          borderColor: formTypeConfig[form.formType]?.color || '#1976d2',
                          color: formTypeConfig[form.formType]?.color || '#1976d2',
                          '&:hover': {
                            borderColor: formTypeConfig[form.formType]?.color || '#1976d2',
                            backgroundColor: alpha(formTypeConfig[form.formType]?.color || '#1976d2', 0.08)
                          }
                        }}
                      >
                        View Details
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ))
              :
              <Typography variant="body2" color="text.secondary">
                You haven't been selected for any opportunities yet.
              </Typography>
            }
          </Paper>
        </Box>

      </Container>

      <AddSkillsDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onSave={handleAddExperience}
      />

      <EditExperienceDialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        onSave={handleEditExperience}
        experience={currentExperience}
      />

      <Dialog
        open={openDeleteConfirm}
        onClose={() => setOpenDeleteConfirm(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this experience? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteConfirm(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteExperience} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <AddCertificateDialog
        open={openCertificateDialog}
        onClose={() => setOpenCertificateDialog(false)}
        onSave={handleAddCertificate}
      />

      <EditCertificateDialog
        open={openEditCertificateDialog}
        onClose={() => setOpenEditCertificateDialog(false)}
        onSave={handleEditCertificate}
        certificate={currentCertificate}
      />

      <Dialog
        open={openDeleteCertificateConfirm}
        onClose={() => setOpenDeleteCertificateConfirm(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this certificate? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteCertificateConfirm(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteCertificate} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Add the missing icons imports
const Person = Email;
const Class = School;
const Grade = School;
const Report = School;
const GradeOutlined = School;
const WatchLater = CalendarMonth;
const Public = LocationOn;

export default Profile;